<script>
import { camelCase } from 'lodash'
import { mapGetters } from 'vuex'

export default {
  name: 'DataTableSearchCriteriaPresets',
  emits: ['update:filters', 'open', 'close', 'hidden'],
  props: {
    filters: {
      type: Object,
    },
    searchCriteria: {
      type: Object,
      required: true,
    },
    isNarrow: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      skipFiltersWatcher: false,
      timeoutToCancel: null,
    }
  },
  computed: {
    ...mapGetters('currentContext', ['currentEvent']),
    ...mapGetters('session', { getEventOrganizationUser: 'eventOrganizationUser' }),
    eventOrganizationUser() {
      return this.getEventOrganizationUser(this.$route.params.eventSlug)
    },
    storedName() {
      return this.getParentNameIfTable(this.$parent)
    },
    anyPresetSaved() {
      return !!this.eouTableFilterConfigs.length
    },
    eouTableFilterConfigs() {
      return this.eventOrganizationUser?.filterConfig?.[this.storedName] || []
    },
    alphabeticalEouTableFilterConfigs() {
      return [...this.eouTableFilterConfigs].sort((a, b) =>
        a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1,
      )
    },
    defaultGlobalTableFilterConfig() {
      return this.globalTableFilterConfigs.find(globalConfig => globalConfig.default === true)
    },
    globalTableFilterConfigs() {
      return this.currentEvent?.globalFilterConfig?.[this.storedName] || []
    },
    alphabeticalGlobalTableFilterConfigs() {
      return [...this.globalTableFilterConfigs].sort((a, b) =>
        a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1,
      )
    },
    anyFilterConfigsPresent() {
      return !!(this.eouTableFilterConfigs.length || this.globalTableFilterConfigs.length)
    },
    activeFilterCount() {
      return Object.keys(this.filters ?? {}).filter(
        k => !!this.filters[k] || this.filters[k] == 0 || this.filters[k] == false,
      ).length
    },
  },
  watch: {
    filters() {
      if (this.skipFiltersWatcher) {
        this.skipFiltersWatcher = false
      } else {
        this.$refs?.searchCriteriaItem?.deselect()
      }
    },
  },
  unmounted() {
    if (this.timeoutToCancel) {
      clearTimeout(this.timeoutToCancel)
    }
  },
  methods: {
    updateFiltersFromInside(newVal) {
      if (this.timeoutToCancel) {
        clearTimeout(this.timeoutToCancel)
      }
      this.skipFiltersWatcher = true
      this.$emit('update:filters', newVal)
      // let's introduce "fuse"
      this.timeoutToCancel = setTimeout(() => {
        this.skipFiltersWatcher = false
      }, 500)
    },
    getParentNameIfTable(comp, level = 0) {
      if (level > 10) {
        return null
      }
      if (comp.$options.name?.endsWith('Table')) {
        return camelCase(comp.$options.name)
      }
      if (comp.$parent) {
        return this.getParentNameIfTable(comp.$parent, level + 1)
      }
      return null
    },
  },
}
</script>

<template lang="pug">
.data-table-filter-presets.btn-group.btn-group-sm
  data-table-search-criteria(
    ref="searchCriteriaItem"
    :filters="filters"
    :searchCriteria="searchCriteria"
    :tableName="storedName"
    @update:filters="updateFiltersFromInside($event)"
    useKeyboardShortcuts
    withPresets
  )
    template(#default="{ anyFilterSet }")
      button.btn.btn-md.btn-outline-primary.with-icon.reverse(
        :class="{ 'btn-outline-warning': anyFilterSet, 'filter-btn': anyFilterConfigsPresent, 'no-right-radius': anyFilterConfigsPresent, 'only-icon': isNarrow }"
        name="filterButton"
      )
        i.mi-outlined.mi-filter-list
        span(v-if="!isNarrow") Filter
        .active-filter-count(v-if="activeFilterCount") {{ activeFilterCount }}
        .mi-outlined.mi-expand-more(v-if="!isNarrow")
  .dropdown(v-if="anyFilterConfigsPresent")
    .btn.btn-outline-primary.no-left-radius.no-left-border.btn-md.only-icon(
      data-bs-toggle="dropdown"
    )
      i.mi-outlined.mi-public
      i.mi-outlined.mi-settings.fixed-config-icon

    ul.dropdown-menu.dropdown-menu-start
      div(v-if="eouTableFilterConfigs.length")
        li
          h6.dropdown-header.bold My configs
        li(v-for="(filterConfig, index) in alphabeticalEouTableFilterConfigs" :key="index")
          a.dropdown-item(
            :name="filterConfig.name"
            @click="updateFiltersFromInside(filterConfig?.value)"
          )
            | {{ filterConfig.name }}
            span.ms-1.text-black-50.small(v-if="filterConfig.default") (user default)
        div(v-if="!!globalTableFilterConfigs.length")
          li
            hr.dropdown-divider
      div(v-if="!!globalTableFilterConfigs.length")
        li
          h6.dropdown-header.bold Global configs
        li(
          v-for="(globalFilterConfig, index) in alphabeticalGlobalTableFilterConfigs"
          :key="index"
        )
          a.dropdown-item(
            :name="globalFilterConfig.name"
            @click="updateFiltersFromInside(globalFilterConfig?.value)"
          )
            | {{ globalFilterConfig.name }}
            span.ms-1.text-black-50.small(v-if="globalFilterConfig.default") (global default)
</template>

<style scoped lang="scss">
@use 'sass:map';
@use 'frontend_stylesheets/theme-variables' as themeVariables;

.active-filter-count {
  background: map.get(themeVariables.$colors, primary);
  border-radius: 100px;
  color: map.get(themeVariables.$colors, onPrimary);
  display: flex;
  align-items: center;
  padding: 0 4px;
  font-weight: 600;
  font-size: 12px;
  height: 18px;
  min-width: 18px;
  justify-content: center;
}

.separator {
  height: 28px;
  margin-top: 6px;
  width: 1px;
  background: map.get(themeVariables.$colors, outlineVariant);
  //margin: 0 4px;
}

.fixed-config-icon {
  position: absolute;
  left: 18px;
  top: 18px;
  font-size: 12px;
  background: map.get(themeVariables.$colors, containerHigh);
  padding: 1px;
  border-radius: 100px;

  .btn:hover & {
    background: map.get(themeVariables.$colors, containerHighest);
  }
}
</style>
